import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  IconButton
} from '@mui/material';
import { AddPhotoAlternate, ContentPaste, Delete } from '@mui/icons-material';
import axios from '../utils/axiosConfig';

const GalleryForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    featured: false
  });
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB limit

  const validateImage = (file) => {
    if (!file) return null;
    
    if (!file.type.startsWith('image/')) {
      return 'Please select an image file';
    }
    
    if (file.size > MAX_FILE_SIZE) {
      return `Image size must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB`;
    }
    
    return null;
  };

  useEffect(() => {
    // Add paste event listener
    const handlePaste = async (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i <items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const file = items[i].getAsFile();
          if (file) {
            const error = validateImage(file);
            if (error) {
              setError(error);
              return;
            }
            setError('');
            setImage(file);
            setPreview(URL.createObjectURL(file));
            break;
          }
        }
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer?.files[0];
    if (file) {
      const error = validateImage(file);
      if (error) {
        setError(error);
        return;
      }
      setError('');
      setImage(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'featured' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const error = validateImage(file);
      if (error) {
        setError(error);
        return;
      }
      setError('');
      setImage(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleClearImage = () => {
    setImage(null);
    setPreview('');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!image) {
      setError('Please select an image');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('featured', formData.featured);
      formDataToSend.append('image', image);

      await axios.post('/api/v2/gallery', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData({ title: '', featured: false });
      setImage(null);
      setPreview('');
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err.response?.data?.message || 'Error uploading image');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        Add New Gallery Image
      </Typography>

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Title"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          required
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={formData.featured}
              onChange={handleInputChange}
              name="featured"
            />
          }
          label="Featured Image"
          sx={{ mb: 2, display: 'block' }}
        />

        <Box 
          sx={{ 
            mb: 3,
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            bgcolor: 'action.hover',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.selected'
            }
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <Box sx={{ mb: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="image-upload"
              type="file"
              onChange={handleImageChange}
            />
            <label htmlFor="image-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<AddPhotoAlternate />}
              >
                Select Image
              </Button>
            </label>
          </Box>

          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}
          >
            <ContentPaste fontSize="small" />
            Press Ctrl+V to paste or drag & drop image here
          </Typography>
        </Box>

        {preview && (
          <Box sx={{ mb: 3, position: 'relative' }}>
            <IconButton
              onClick={handleClearImage}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <Delete />
            </IconButton>
            <img
              src={preview}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '300px',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
          fullWidth
          sx={{
            py: 1.5,
            '&:not(:disabled)': {
              background: 'linear-gradient(45deg, #1a237e 30%, #534bae 90%)',
              boxShadow: '0 3px 12px rgba(26, 35, 126, 0.3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #000051 30%, #1a237e 90%)',
                boxShadow: '0 4px 16px rgba(26, 35, 126, 0.4)',
                transform: 'translateY(-1px)'
              }
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          {loading ? <CircularProgress size={24} /> : 'Upload Image'}
        </Button>
      </form>
    </Paper>
  );
};

export default GalleryForm;
